import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getConfigPublicacao, ConfigPublicacao } from '../../services/configPublicacoesService';
import swal from 'sweetalert';
import { getRelativeFileUrl } from '../../services/relativeFilesService';

const ConfigPubDetail: React.FC = () => {
  const { cfgId } = useParams<{ cfgId: string }>();
  const [config, setConfig] = useState<ConfigPublicacao | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchConfig = async () => {
    if (!cfgId) return;
    setLoading(true);
    try {
      const data = await getConfigPublicacao(cfgId);
      setConfig(data);
    } catch (error) {
      console.error('Erro ao obter configuração:', error);
      swal('Erro', 'Não foi possível obter a configuração de publicação.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [cfgId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Typography variant="h6" color="error">
        Configuração não encontrada.
      </Typography>
    );
  }

  // Montar URL do brasão, se houver
  const emblemUrl = config.emblem_filename
    ? getRelativeFileUrl(config.emblem_filename)
    : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Detalhes da Configuração de Publicação</Typography>
        <Button
          variant="contained"
          onClick={() => navigate(`/configuracoes-publicacoes/${config.id}/editar`)}
        >
          Editar
        </Button>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <strong>ID (Entidade):</strong> {config.id}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Prefixo:</strong> {config.prefix}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Sufixo:</strong> {config.suffix}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Tipo:</strong> {config.type}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Gestor:</strong> {config.manager_name}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Editor:</strong> {config.editor_name}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          <strong>Endereço:</strong> {config.address.street}, CEP {config.address.cep}
        </Typography>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          <strong>Habilitado para DOM:</strong> {config.dom_enabled ? 'Sim' : 'Não'}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Habilitado para DOU:</strong> {config.dou_enabled ? 'Sim' : 'Não'}
        </Typography>
        <Typography variant="subtitle2">
          <strong>Habilitado para JGC:</strong> {config.jgc_enabled ? 'Sim' : 'Não'}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            <strong>Dados do Cabeçalho:</strong>
          </Typography>
          <Typography variant="body2">
            <strong>Ano em Romano (início):</strong> {config.header.year_roman_start_from}
          </Typography>
          <Typography variant="body2">
            <strong>Edição (início):</strong> {config.header.edition_number_start_from}
          </Typography>
          <Typography variant="body2">
            <strong>Título:</strong> {config.header.title}
          </Typography>
          <Typography variant="body2">
            <strong>Subtítulo:</strong> {config.header.subtitle}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            <strong>Brasão:</strong>
          </Typography>
          {emblemUrl ? (
            <Box
              component="img"
              src={emblemUrl}
              alt="Brasão"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                border: '1px solid #ccc',
                borderRadius: 2,
                mt: 1,
              }}
            />
          ) : (
            <Typography variant="body2">Nenhum brasão configurado.</Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ConfigPubDetail;